//#region Imports

import { Injectable } from '@angular/core';
import { Tenant } from '../../models/interfaces/tenant.interface';

//#endregion

@Injectable({
  providedIn: 'root'
})
export class TenantThemeService {

  //#region Private Properties

  private readonly DEFAULT_PRIMARY_COLOR = '#4A3AFF';

  private readonly TENANT_PRIMARY_COLOR_STORAGE_KEY = 'tenant-primary-color';

  //#endregion

  //#region Public Methods

  public applyTenantTheme(tenant: Tenant | null): void {
    const primaryColor = tenant?.primaryColor ?? this.DEFAULT_PRIMARY_COLOR;
    this.applyColor(primaryColor);
  }

  public resetTheme(): void {
    this.applyColor(this.DEFAULT_PRIMARY_COLOR);
  }

  public getCurrentPrimaryColor(): string {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--tenant-primary-color') || this.DEFAULT_PRIMARY_COLOR;
  }

  public saveInitialThemeColor(tenant: Tenant | null): void {
    const currentColor = this.getCurrentPrimaryColor().trim();
    const primaryColor = (tenant?.primaryColor ?? currentColor) || this.DEFAULT_PRIMARY_COLOR;

    localStorage.setItem(this.TENANT_PRIMARY_COLOR_STORAGE_KEY, primaryColor);
  }

  public applyStoredTheme(): void {
    const storedPrimaryColor = localStorage.getItem(this.TENANT_PRIMARY_COLOR_STORAGE_KEY);

    if (storedPrimaryColor) {
      this.applyColor(storedPrimaryColor);
      return;
    }

    this.resetTheme();
  }

  //#endregion

  //#region Private Methods

  private applyColor(primaryColor: string): void {
    document.documentElement.style.setProperty('--tenant-primary-color', primaryColor);
    document.documentElement.style.setProperty('--color-lPrimary', primaryColor);

    const primaryLightColor = this.getLighterShade(primaryColor, 0.1);
    document.documentElement.style.setProperty('--tenant-primary-light-color', primaryLightColor);

    const shadowPrimaryColor = this.hexToRgba(primaryColor, 0.3);
    const shadowPrimaryLightColor = this.hexToRgba(primaryColor, 0.15);
    const shadowPrimaryLight2Color = this.hexToRgba(primaryLightColor, 0.3);
    const shadowPrimaryLight3Color = this.hexToRgba(primaryLightColor, 0.15);

    document.documentElement.style.setProperty('--shadow-primary-color', shadowPrimaryColor);
    document.documentElement.style.setProperty('--shadow-primary-light-color', shadowPrimaryLightColor);
    document.documentElement.style.setProperty('--shadow-primary-light-color-2', shadowPrimaryLight2Color);
    document.documentElement.style.setProperty('--shadow-primary-light-color-3', shadowPrimaryLight3Color);
  }

  private hexToRgba(hex: string, alpha: number): string {
    hex = hex.replace('#', '');
    
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  private getLighterShade(hex: string, percent: number): string {
    hex = hex.replace('#', '');

    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);
    
    r = Math.min(255, Math.floor(r + (255 - r) * percent));
    g = Math.min(255, Math.floor(g + (255 - g) * percent));
    b = Math.min(255, Math.floor(b + (255 - b) * percent));

    const rHex = r.toString(16).padStart(2, '0');
    const gHex = g.toString(16).padStart(2, '0');
    const bHex = b.toString(16).padStart(2, '0');
    
    return `#${rHex}${gHex}${bHex}`;
  }

  //#endregion
}
